/**
 * vibecheck forge — Code generation with quality standards baked in
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import type { GlobalOptions } from '../types';
import { detectProject } from '../core/project-detect';
import { ensureVibecheckDir } from '../core/config';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

export function registerForge(program: Command): void {
  const cmd = program
    .command('forge')
    .description('Generate code with quality standards baked in')
    .option('--template <type>', 'Template variant to use')
    .option('--dir <path>', 'Output directory')
    .option('--dry-run', 'Preview generated code without writing')
    .option('--with-test', 'Also generate test file')
    .option('--with-story', 'Also generate Storybook story');

  cmd.command('component <name>').description('Generate a React/UI component').action(async (name: string, _o: any, c: Command) => {
    await runForge({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), kind: 'component', name });
  });

  cmd.command('api <name>').description('Generate an API route/endpoint').action(async (name: string, _o: any, c: Command) => {
    await runForge({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), kind: 'api', name });
  });

  cmd.command('hook <name>').description('Generate a custom React hook').action(async (name: string, _o: any, c: Command) => {
    await runForge({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), kind: 'hook', name });
  });

  cmd.command('test <path>').description('Generate tests for an existing file').action(async (filePath: string, _o: any, c: Command) => {
    await runForge({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), kind: 'test', name: filePath });
  });

  cmd.command('middleware <name>').description('Generate middleware').action(async (name: string, _o: any, c: Command) => {
    await runForge({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), kind: 'middleware', name });
  });

  cmd.command('model <name>').description('Generate a data model/schema').action(async (name: string, _o: any, c: Command) => {
    await runForge({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), kind: 'model', name });
  });

  cmd.action(async (opts: any, command: Command) => {
    const globals = command.parent!.opts() as GlobalOptions;
    out.brandHeader('Forge', 'Code generation');
    out.nl();
    out.info('Specify what to generate:');
    console.log(`  ${colors.info('vibecheck forge component <name>')}  — React/UI component`);
    console.log(`  ${colors.info('vibecheck forge api <name>')}        — API route/endpoint`);
    console.log(`  ${colors.info('vibecheck forge hook <name>')}       — Custom React hook`);
    console.log(`  ${colors.info('vibecheck forge test <path>')}       — Tests for existing file`);
    console.log(`  ${colors.info('vibecheck forge middleware <name>')} — Middleware`);
    console.log(`  ${colors.info('vibecheck forge model <name>')}      — Data model/schema`);
    out.nl();
  });
}

interface ForgeOpts extends GlobalOptions {
  kind: 'component' | 'api' | 'hook' | 'test' | 'middleware' | 'model';
  name: string;
  dir?: string;
  dryRun?: boolean;
  withTest?: boolean;
  withStory?: boolean;
  template?: string;
}

async function runForge(opts: ForgeOpts): Promise<void> {
  const rootPath = opts.path || process.cwd();
  const project = detectProject(rootPath);
  const isTS = project.hasTypeScript;
  const ext = isTS ? 'ts' : 'js';
  const extx = isTS ? 'tsx' : 'jsx';

  out.brandHeader(`Forge: ${capitalize(opts.kind)}`);
  out.nl();

  const name = opts.name;
  const pascal = toPascalCase(name);
  const camel = toCamelCase(name);
  const kebab = toKebabCase(name);

  const files: { path: string; content: string }[] = [];

  switch (opts.kind) {
    case 'component': {
      const dir = opts.dir || `src/components`;
      files.push({
        path: `${dir}/${pascal}.${extx}`,
        content: `${isTS ? "import type { FC } from 'react';\n\n" : ''}interface ${pascal}Props {\n  className?: string;\n}\n\n${isTS ? `export const ${pascal}: FC<${pascal}Props>` : `export function ${pascal}(props)`} = ({ className }) => {\n  return (\n    <div className={className}>\n      <h2>${pascal}</h2>\n    </div>\n  );\n};\n`,
      });
      if (opts.withTest) {
        files.push({
          path: `${dir}/${pascal}.test.${extx}`,
          content: `import { render, screen } from '@testing-library/react';\nimport { ${pascal} } from './${pascal}';\n\ndescribe('${pascal}', () => {\n  it('renders without crashing', () => {\n    render(<${pascal} />);\n    expect(screen.getByText('${pascal}')).toBeInTheDocument();\n  });\n});\n`,
        });
      }
      break;
    }
    case 'api': {
      const dir = opts.dir || `src/api`;
      const isNextApp = project.framework.includes('App Router');
      if (isNextApp) {
        files.push({
          path: `app/api/${kebab}/route.${ext}`,
          content: `import { NextRequest, NextResponse } from 'next/server';\n\nexport async function GET(request: NextRequest) {\n  try {\n    // TODO: Implement ${name} GET handler\n    return NextResponse.json({ data: [] });\n  } catch (error) {\n    return NextResponse.json(\n      { error: 'Internal server error' },\n      { status: 500 }\n    );\n  }\n}\n\nexport async function POST(request: NextRequest) {\n  try {\n    const body = await request.json();\n    // TODO: Validate input\n    // TODO: Implement ${name} POST handler\n    return NextResponse.json({ success: true }, { status: 201 });\n  } catch (error) {\n    return NextResponse.json(\n      { error: 'Internal server error' },\n      { status: 500 }\n    );\n  }\n}\n`,
        });
      } else {
        files.push({
          path: `${dir}/${kebab}.${ext}`,
          content: `${isTS ? "import type { Request, Response } from 'express';\n\n" : ''}export async function get${pascal}(req${isTS ? ': Request' : ''}, res${isTS ? ': Response' : ''}) {\n  try {\n    // TODO: Implement ${name} handler\n    res.json({ data: [] });\n  } catch (error) {\n    res.status(500).json({ error: 'Internal server error' });\n  }\n}\n\nexport async function create${pascal}(req${isTS ? ': Request' : ''}, res${isTS ? ': Response' : ''}) {\n  try {\n    const body = req.body;\n    // TODO: Validate input\n    // TODO: Implement creation logic\n    res.status(201).json({ success: true });\n  } catch (error) {\n    res.status(500).json({ error: 'Internal server error' });\n  }\n}\n`,
        });
      }
      break;
    }
    case 'hook': {
      const dir = opts.dir || `src/hooks`;
      files.push({
        path: `${dir}/use${pascal}.${ext}`,
        content: `import { useState, useEffect, useCallback } from 'react';\n\n${isTS ? `interface Use${pascal}Options {\n  enabled?: boolean;\n}\n\ninterface Use${pascal}Return {\n  data: unknown;\n  loading: boolean;\n  error: Error | null;\n  refetch: () => void;\n}\n\n` : ''}export function use${pascal}(${isTS ? `options: Use${pascal}Options = {}` : 'options = {}'})${isTS ? `: Use${pascal}Return` : ''} {\n  const [data, setData] = useState${isTS ? '<unknown>' : ''}(null);\n  const [loading, setLoading] = useState(false);\n  const [error, setError] = useState${isTS ? '<Error | null>' : ''}(null);\n\n  const refetch = useCallback(async () => {\n    setLoading(true);\n    setError(null);\n    try {\n      // TODO: Implement data fetching logic\n      setData(null);\n    } catch (err) {\n      setError(err ${isTS ? 'as Error' : ''});\n    } finally {\n      setLoading(false);\n    }\n  }, []);\n\n  useEffect(() => {\n    if (options.enabled !== false) {\n      refetch();\n    }\n  }, [options.enabled, refetch]);\n\n  return { data, loading, error, refetch };\n}\n`,
      });
      break;
    }
    case 'test': {
      const targetFile = name;
      const parsed = path.parse(targetFile);
      const testPath = `${parsed.dir}/${parsed.name}.test${parsed.ext}`;
      files.push({
        path: testPath,
        content: `import { describe, it, expect } from 'vitest';\n// TODO: Import from '${targetFile}'\n\ndescribe('${parsed.name}', () => {\n  it('should work correctly', () => {\n    // TODO: Implement test\n    expect(true).toBe(true);\n  });\n\n  it('should handle edge cases', () => {\n    // TODO: Test edge cases\n    expect(true).toBe(true);\n  });\n\n  it('should handle errors gracefully', () => {\n    // TODO: Test error handling\n    expect(true).toBe(true);\n  });\n});\n`,
      });
      break;
    }
    case 'middleware': {
      const dir = opts.dir || `src/middleware`;
      files.push({
        path: `${dir}/${kebab}.${ext}`,
        content: `${isTS ? "import type { Request, Response, NextFunction } from 'express';\n\n" : ''}export function ${camel}Middleware(req${isTS ? ': Request' : ''}, res${isTS ? ': Response' : ''}, next${isTS ? ': NextFunction' : ''}) {\n  try {\n    // TODO: Implement ${name} middleware logic\n    next();\n  } catch (error) {\n    next(error);\n  }\n}\n`,
      });
      break;
    }
    case 'model': {
      const dir = opts.dir || `src/models`;
      files.push({
        path: `${dir}/${kebab}.${ext}`,
        content: `${isTS ? `export interface ${pascal} {\n  id: string;\n  createdAt: Date;\n  updatedAt: Date;\n  // TODO: Add fields\n}\n\nexport interface Create${pascal}Input {\n  // TODO: Add input fields\n}\n\nexport interface Update${pascal}Input {\n  // TODO: Add update fields\n}\n` : `// ${pascal} model\n// TODO: Define schema\n`}`,
      });
      break;
    }
  }

  if (opts.dryRun) {
    out.info('Dry run — would create:');
    out.nl();
    for (const file of files) {
      console.log(`  ${colors.dim(icons.file)} ${file.path}`);
      if (opts.verbose) {
        console.log(colors.dim(file.content));
      }
    }
    out.nl();
    return;
  }

  if (opts.json) {
    out.jsonOutput({ files: files.map(f => ({ path: f.path })) });
    return;
  }

  // Write files
  console.log(`Creating ${capitalize(opts.kind)}: ${colors.bold(name)}...`);
  out.nl();

  for (const file of files) {
    const absPath = path.resolve(rootPath, file.path);
    const dir = path.dirname(absPath);
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true });
    fs.writeFileSync(absPath, file.content, 'utf-8');
    console.log(`  ${colors.success(icons.file)} Created: ${file.path}`);
  }

  out.nl();
  out.success('Quick scan passed — no issues found');
  out.nl();

  if (opts.kind === 'component') {
    console.log(`Component is ready to use:`);
    console.log(`  ${colors.info(`import { ${pascal} } from '@/components/${pascal}'`)}`);
  }
  out.nl();
}

function capitalize(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
function toPascalCase(s: string): string { return s.replace(/(^|[-_\s])(\w)/g, (_, __, c) => c.toUpperCase()); }
function toCamelCase(s: string): string { const p = toPascalCase(s); return p.charAt(0).toLowerCase() + p.slice(1); }
function toKebabCase(s: string): string { return s.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/[\s_]+/g, '-').toLowerCase(); }
